'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '../../toast/ToastProvider';
import { EMPTY_STAGED_DOCUMENTATION_FILES } from './applicationDocumentation.constants';
import type {
  ApplicationDocumentationController,
  ApplicationDocumentationFile,
  DocumentationDisplayFile,
  DocumentationFileCategory,
  StagedDocumentationFiles,
} from './applicationDocumentation.types';
import {
  getVisibleDocumentationFiles,
  resolveStagedDocumentationFileRemoval,
} from './applicationDocumentationFiles.utils';
import type { PersistDocumentationUrls } from './usePersistDocumentationUrls';

const DOCUMENTATION_FILE_CATEGORIES: DocumentationFileCategory[] = [
  'application',
  'api',
  'testing-reports',
];

export type StagedDocumentationState = {
  visibleFilesByCategory: Record<
    DocumentationFileCategory,
    DocumentationDisplayFile[]
  >;
  visibleDocumentationUrls: string[];
  stageFiles: ({
    category,
    files,
  }: {
    category: DocumentationFileCategory;
    files: File[];
  }) => void;
  stageFileRemoval: ({
    category,
    fileIndex,
    files,
  }: {
    category: DocumentationFileCategory;
    fileIndex: number;
    files: ApplicationDocumentationFile[];
  }) => void;
  stageDocumentationUrls: (urls: string[]) => void;
  persistDeferredDocumentation: () => Promise<boolean>;
};

export const useStagedDocumentation = ({
  applicationId,
  controller,
  isDeferred,
  persistDocumentationUrls,
}: {
  applicationId: string | null;
  controller: ApplicationDocumentationController;
  isDeferred: boolean;
  persistDocumentationUrls: PersistDocumentationUrls;
}): StagedDocumentationState => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const {
    applicationDocumentationFiles,
    apiDocumentationFiles,
    previousTestingReportsFiles,
    documentationUrls,
    uploadFiles,
    deleteFile,
  } = controller;
  const [stagedFiles, setStagedFiles] = useState<StagedDocumentationFiles>(
    EMPTY_STAGED_DOCUMENTATION_FILES,
  );
  const [stagedDeletedFileIds, setStagedDeletedFileIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const [stagedDocumentationUrls, setStagedDocumentationUrls] = useState<
    string[] | null
  >(null);

  const visibleFilesByCategory = useMemo(() => {
    const persistedByCategory: Record<
      DocumentationFileCategory,
      ApplicationDocumentationFile[]
    > = {
      application: applicationDocumentationFiles,
      api: apiDocumentationFiles,
      'testing-reports': previousTestingReportsFiles,
    };

    return {
      application: getVisibleDocumentationFiles({
        files: persistedByCategory.application,
        stagedFiles: stagedFiles.application,
        stagedDeletedFileIds,
      }),
      api: getVisibleDocumentationFiles({
        files: persistedByCategory.api,
        stagedFiles: stagedFiles.api,
        stagedDeletedFileIds,
      }),
      'testing-reports': getVisibleDocumentationFiles({
        files: persistedByCategory['testing-reports'],
        stagedFiles: stagedFiles['testing-reports'],
        stagedDeletedFileIds,
      }),
    };
  }, [
    apiDocumentationFiles,
    applicationDocumentationFiles,
    previousTestingReportsFiles,
    stagedDeletedFileIds,
    stagedFiles,
  ]);

  const visibleDocumentationUrls = stagedDocumentationUrls ?? documentationUrls;

  const stageFiles = useCallback(
    ({
      category,
      files,
    }: {
      category: DocumentationFileCategory;
      files: File[];
    }) => {
      setStagedFiles((previousFiles) => ({
        ...previousFiles,
        [category]: [...previousFiles[category], ...files],
      }));
    },
    [],
  );

  const stageFileRemoval = useCallback(
    ({
      category,
      fileIndex,
      files,
    }: {
      category: DocumentationFileCategory;
      fileIndex: number;
      files: ApplicationDocumentationFile[];
    }) => {
      const removal = resolveStagedDocumentationFileRemoval({
        files,
        stagedDeletedFileIds,
        fileIndex,
      });

      if (removal.type === 'persisted') {
        setStagedDeletedFileIds(
          (previousIds) => new Set([...previousIds, removal.fileId]),
        );

        return;
      }

      setStagedFiles((previousFiles) => ({
        ...previousFiles,
        [category]: previousFiles[category].filter(
          (_, currentIndex) => currentIndex !== removal.stagedFileIndex,
        ),
      }));
    },
    [stagedDeletedFileIds],
  );

  const persistDeferredDocumentation = useCallback(async () => {
    if (!isDeferred) {
      return true;
    }

    const categoriesWithStagedFiles = DOCUMENTATION_FILE_CATEGORIES.filter(
      (category) => stagedFiles[category].length > 0,
    );
    const hasStagedChanges =
      categoriesWithStagedFiles.length > 0 ||
      stagedDeletedFileIds.size > 0 ||
      stagedDocumentationUrls !== null;

    if (!hasStagedChanges) {
      return true;
    }

    if (!applicationId) {
      return false;
    }

    try {
      for (const fileId of stagedDeletedFileIds) {
        await deleteFile({ applicationId, fileId });
      }

      for (const category of categoriesWithStagedFiles) {
        await uploadFiles({
          applicationId,
          category,
          files: stagedFiles[category],
        });
      }

      if (stagedDocumentationUrls !== null) {
        const didSave = await persistDocumentationUrls({
          urls: stagedDocumentationUrls,
        });

        if (!didSave) {
          return false;
        }
      }

      setStagedFiles(EMPTY_STAGED_DOCUMENTATION_FILES);
      setStagedDeletedFileIds(new Set());
      setStagedDocumentationUrls(null);

      return true;
    } catch (error) {
      console.error(
        new Error('Failed to persist staged documentation', {
          cause: error instanceof Error ? error : new Error(String(error)),
        }),
      );
      toasterApi.error({
        title: t('errors.failedToUploadFiles'),
        description: t('errors.pleaseTryAgain'),
      });

      return false;
    }
  }, [
    applicationId,
    deleteFile,
    isDeferred,
    persistDocumentationUrls,
    stagedDeletedFileIds,
    stagedDocumentationUrls,
    stagedFiles,
    t,
    uploadFiles,
  ]);

  return {
    visibleFilesByCategory,
    visibleDocumentationUrls,
    stageFiles,
    stageFileRemoval,
    stageDocumentationUrls: setStagedDocumentationUrls,
    persistDeferredDocumentation,
  };
};
